import React, { useRef } from 'react';
import Button from '../Button';
import { composeWord } from '../../domain/compose';
import { fileSizeText } from '../../lib/fileLimits';
import UploadProgressList from '../UploadProgress';
import s from './drawers.module.css';

// New Message's Attach Files (#605): MMS's button under the message, which
// opens the computer's file chooser — one file per pick, any type, no Online
// Resources picker. Each pick is uploaded at once and listed under the button
// with its size and a × that takes it off the message. While it goes up, or
// once it has failed, it is the upload row every upload site draws
// (src/components/UploadProgress.jsx, #612): the bar and the amount, or the
// sentence and Try again — the failed file stays on the message, as on both
// phones. The state, the bounds and the upload are `useNewMessage`'s
// (src/hooks/useNewMessage.js); the words are the core's (core/src/compose.rs).

export default function MessageAttachments({ composer }) {
  const input = useRef(null);
  const files = composer.attachments || [];
  const ready = files.filter((f) => f.status === 'ready');
  const going = files.filter((f) => f.status !== 'ready').map((f) => ({ ...f, total: f.size }));
  return (
    <div className={s.section}>
      <Button variant="secondary" onClick={() => input.current?.click()}>{composeWord('attach')}</Button>
      <input
        ref={input}
        type="file"
        aria-label={composeWord('attach')}
        // No `multiple` and no `accept`: MMS's chooser takes one file of any
        // type at a time, and the core refuses what an email cannot carry.
        style={{ display: 'none' }}
        onChange={(e) => {
          // Cleared first, so picking the same file again still fires.
          const file = e.target.files?.[0] || null;
          e.target.value = '';
          if (file) composer.attachFile(file);
        }}
      />
      {ready.length ? (
        <ul className={s.recipients} aria-label={composeWord('attach')}>
          {ready.map((f) => (
            <li key={f.key} className={s.chip}>
              <span>
                {f.name}
                {' · '}
                {fileSizeText(f.size)}
              </span>
              <button
                type="button"
                className={s.chipRemove}
                aria-label={`${composeWord('attach_remove')} ${f.name}`}
                onClick={() => composer.detachFile(f.key)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <UploadProgressList
        items={going}
        label={composeWord('attach_uploading')}
        onRetry={composer.retryFile}
        onCancel={composer.detachFile}
        onDismiss={composer.detachFile}
        cancelLabel={composeWord('attach_remove')}
        dismissLabel={composeWord('attach_remove')}
      />
      <p className={s.hint}>{composeWord('attach_hint')}</p>
    </div>
  );
}
